import { useState, useEffect } from 'react'
import api from '../../utils/api'

/**
 * Component for displaying and editing notes on media objects
 *
 * @param {string} mediaType - The type of media (song, album, playlist, mix, collage)
 * @param {number} mediaId - The ID of the media object
 * @param {function} onLoad - Optional callback that receives true if note exists, false otherwise
 */
function MediaNote({ mediaType, mediaId, onLoad }) {
  const [note, setNote] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchNote = async () => {
      try {
        setIsLoading(true)

        // Fetch note for this media object
        const response = await api.get(`notes/?media_type=${mediaType}&media_id=${mediaId}`)

        const noteText = response.data.text || ''
        setNote(noteText)

        // Notify parent component if we have a note
        if (onLoad) {
          onLoad(noteText.length > 0)
        }
      } catch (error) {
        console.error('Error fetching note:', error)
        if (onLoad) {
          onLoad(false)
        }
      } finally {
        setIsLoading(false)
      }
    }

    if (mediaType && mediaId) {
      fetchNote()
    }
  }, [mediaType, mediaId, onLoad])

  const saveNote = async () => {
    try {
      await api.post('notes/save/', {
        media_type: mediaType,
        media_id: mediaId,
        text: note,
      })

      setIsEditing(false)

      // Notify parent component of note status
      if (onLoad) {
        onLoad(note.length > 0)
      }
    } catch (error) {
      console.error('Error saving note:', error)
    }
  }

  const handleKeyDown = (e) => {
    // Save on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      saveNote()
    }
    // Cancel on Escape
    else if (e.key === 'Escape') {
      setIsEditing(false)
    }
  }

  if (isLoading) {
    return null
  }

  // If there's no note and we're not editing, don't render anything
  if (!note && !isEditing) {
    return (
      <div className='left-justify mt-3 text-sm' style={{ padding: '0 10px' }}>
        <div className='text-left'>
          <span
            className='metadata-text secondary cursor-pointer'
            onClick={() => setIsEditing(true)}
          >
            add note
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className='left-justify mt-3 text-sm' style={{ padding: '0 10px' }}>
      {isEditing ? (
        <div className='flex flex-col'>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            className='w-full resize-none border p-1 text-sm'
            rows={3}
            placeholder='Add a note...'
          />
          <div className='mt-1 flex justify-end gap-2'>
            <button
              onClick={() => setIsEditing(false)}
              className='text-gray-500 hover:text-gray-700 text-xs'
            >
              Cancel
            </button>
            <button onClick={saveNote} className='text-blue-600 hover:text-blue-800 text-xs'>
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className='text-left'>
          <span
            className='metadata-text secondary cursor-pointer'
            onClick={() => setIsEditing(true)}
          >
            note:
          </span>
          {note && <span className='metadata-text secondary ml-1'>{note}</span>}
        </div>
      )}
    </div>
  )
}

export default MediaNote
